'use client'

import Link from 'next/link'
import AffiliateLink from '@/components/common/AffiliateLink'
import { isAffiliateUrl } from '@/lib/affiliates/metadata'

// Bento tile sizes for N cards. Composed only from "bands" that each fill the
// 4-col grid exactly, so the layout packs with zero gaps for ANY count — this is
// what makes the same image-cover tile look reusable everywhere (blog articles,
// related-article teasers, city catalogs). Bands used:
const A = ['xl', 'tall', 'std', 'std']   // anchor band  → 4 cols × 2 rows (one big focal tile)
const WW = ['wide', 'wide']              // banner band  → 4 cols × 1 row
const Wss = ['wide', 'std', 'std']       // banner+small → 4 cols × 1 row
const S4 = ['std', 'std', 'std', 'std']  // small band   → 4 cols × 1 row
// Key rule: at most ONE anchor (big xl) per ~3 rows, so big tiles are never
// stacked one under another. Small/teaser counts (incl. the 3/6/9 bucket
// outputs) get hand-tuned single-anchor layouts.
export const bentoSizes = (n) => {
    if (n <= 0) return []
    if (n === 1) return ['hero']                       // full-width single tile
    if (n === 2) return ['xl', 'xl']                   // two big tiles side by side
    if (n === 3) return ['xl', 'wide', 'wide']         // anchor + 2 banners stacked beside it
    if (n === 4) return [...A]
    if (n === 5) return ['xl', 'std', 'std', 'std', 'std']
    if (n === 6) return [...A, ...WW]
    if (n === 7) return [...A, ...Wss]
    if (n === 8) return [...A, ...S4]
    if (n === 9) return [...A, ...WW, ...Wss]          // one anchor, no stacked xl

    // Longer feeds (full listings): one lead anchor, then a CYCLE of band types
    // (banner → smalls → anchor) so long grids mix tile sizes instead of
    // degenerating into a wall of identical squares; anchors stay spaced.
    const out = [...A]
    let rem = n - 4
    if (rem % 2 === 1) { out.push(...Wss); rem -= 3 }  // fix parity once → rem stays even
    const cycle = [WW, S4, A]
    let bi = 0
    while (rem > 0) {
        const band = cycle[bi % cycle.length]
        if (band.length <= rem) { out.push(...band); rem -= band.length }
        else { out.push(...WW); rem -= 2 }             // хвост из 2 — баннерная лента
        bi += 1
    }
    return out
}

// Snap a shown count to a balanced bento size: 3-5 → 3, 6-8 → 6, 9+ → 9 (1-2 stay
// as-is). Keeps embedded teaser blocks (related articles, top sights, …) full and
// tidy. Full listings/catalogs opt out and show everything.
export const bucketCount = (a) => (a >= 9 ? 9 : a >= 6 ? 6 : a >= 3 ? 3 : a)

/**
 * "Gallery · Field" bento grid: gapless image-cover tiles of varied sizes with
 * the title overlaid on a scrim. Renders pre-mapped items, so it's content-
 * agnostic (blog articles, city guides, …).
 *
 * `items` is [{ id, href, img, alt, cat, title, excerpt, meta, actions }] —
 * `cat` is the overline tag, `meta` the small line under the title (date / area
 * / …). Items without an `href`, or items with explicit `actions`, render as a
 * non-clickable tile (e.g. inline POIs). Optional per-item `target` / `rel` /
 * `onClick` pass through to the tile — for outbound affiliate tiles.
 *
 * `bucket` snaps the count to 3/6/9 for teaser blocks (see bucketCount).
 * `excerptAll` is kept for compatibility; excerpts now render whenever present.
 */
const textScore = (item) => String(item?.title || '').length * 2 + String(item?.excerpt || '').length
const sizeCapacity = { hero: 5, xl: 5, wide: 4, tall: 3, std: 1 }

const imageSizes = (size) => {
    if (size === 'hero') return '100vw'
    const large = size === 'xl' || size === 'wide'
    return `(max-width: 560px) 100vw, (max-width: 900px) ${large ? '100vw' : '50vw'}, ${large ? '50vw' : '25vw'}`
}

const fitItemsToSizes = (items, sizes) => {
    if (items.length < 3) return items

    // Keep the lead/newest article in the lead slot. Fit the remaining articles
    // to the existing balanced layout: longer copy gets roomier wide/tall slots,
    // while short copy fills compact std slots. The size sequence itself stays
    // unchanged, so dense bento packing remains deterministic.
    // Подбор идёт окнами по 4 карточки: сортировка всего списка по длине текста ломала
    // хронологию — на странице категории вторая по свежести статья уезжала в конец из 52
    // (ревью 30.09.2026). В окне порядок сдвигается не больше чем на 3 позиции.
    const fitted = [...items]
    const WINDOW = 4
    for (let start = 1; start < items.length; start += WINDOW) {
        const end = Math.min(start + WINDOW, items.length)
        const content = items.slice(start, end).sort((a, b) => textScore(b) - textScore(a))
        const slots = sizes.slice(start, end).map((size, offset) => ({ index: start + offset, size }))
            .sort((a, b) => (sizeCapacity[b.size] || 1) - (sizeCapacity[a.size] || 1) || a.index - b.index)
        slots.forEach((slot, i) => { fitted[slot.index] = content[i] })
    }
    return fitted
}

export default function GfBento({ items = [], bucket = false, excerptAll = false, prioritizeText = false }) {
    const shown = bucket ? items.slice(0, bucketCount(items.length)) : items
    if (!shown.length) return null
    const sizes = bentoSizes(shown.length)
    const arranged = prioritizeText ? fitItemsToSizes(shown, sizes) : shown
    return (
        <div className={`bl-bento${excerptAll ? ' ex-all' : ''}`}>
            {arranged.map((it, i) => {
                const size = sizes[i] || 'std'
                const actions = Array.isArray(it.actions) ? it.actions.filter((a) => a?.href && a?.label) : []
                const hasActions = actions.length > 0
                // Internal route → Next Link; external/hash/protocol href → plain
                // <a>; no href → non-clickable <div>.
                const external = !hasActions && it.href && /^(https?:|mailto:|tel:|#)/.test(it.href)
                // Партнёрская внешняя ссылка → AffiliateLink (трекинг + /api/r): на /tours
                // плитки городов вели на GetYourGuide голым <a> (аудит 2026-09-16, §4).
                const Tile = hasActions || !it.href ? 'div' : external ? (isAffiliateUrl(it.href) ? AffiliateLink : 'a') : Link
                // Index-suffixed so a missing or duplicated `id` can't collide.
                // `key` must come BEFORE the spread — when it trails a spread the
                // SWC/Turbopack JSX transform can fail to hoist it to the real
                // element key, so React still warns about a keyless list.
                const key = it.id != null ? `${it.id}-${i}` : i
                return (
                    <Tile key={key} className={`mz ${size}`} {...(!hasActions && it.href ? { href: it.href } : {})}
                        {...(!hasActions && it.target ? { target: it.target } : {})}
                        {...(!hasActions && it.rel ? { rel: it.rel } : {})}
                        {...(!hasActions && it.onClick ? { onClick: it.onClick } : {})}
                        {...(Tile === AffiliateLink ? { position: 'bento' } : {})}
                        {...(Tile === Link ? { prefetch: false } : {})}>
                        {it.img && <img src={it.img} srcSet={it.srcSet || undefined} sizes={it.srcSet ? imageSizes(size) : undefined} alt={it.alt} loading="lazy" />}
                        <span className="scrim" />
                        <div className="mz-b">
                            {it.cat && <span className="mz-cat">{it.cat}</span>}
                            <h3>{it.title}</h3>
                            {it.excerpt && <p className="mz-ex">{it.excerpt}</p>}
                            {it.meta && <span className="mz-date">{it.meta}</span>}
                            {hasActions && (
                                <div className="mz-actions">
                                    {actions.map((action, ai) => {
                                        const actionExternal = /^(https?:|mailto:|tel:|#)/.test(action.href)
                                        const Action = actionExternal ? (isAffiliateUrl(action.href) ? AffiliateLink : 'a') : Link
                                        return (
                                            <Action key={`${action.id || action.label}-${ai}`} className={`mz-action${action.className ? ` ${action.className}` : ''}`} href={action.href}
                                                {...(actionExternal && action.target ? { target: action.target } : {})}
                                                {...(actionExternal && action.rel ? { rel: action.rel } : {})}
                                                {...(action.onClick ? { onClick: action.onClick } : {})}
                                                {...(Action === AffiliateLink ? { position: 'bento_action' } : {})}>
                                                {action.label}
                                            </Action>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    </Tile>
                )
            })}
        </div>
    )
}
